import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  ArrowRight,
  CheckCircle2,
  Upload,
  Mail,
  Phone,
  MapPin,
  Clock,
  Briefcase,
  GraduationCap,
  Heart,
  Zap,
  Shield,
  Users,
  TrendingUp,
  Award,
  Code,
  Rocket,
  FileText,
  Send,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { cn } from "../../lib/utils";
import { accordionData } from "./CareerData";

const Careers = () => {
  const initialFormData = {
    fullName: "",
    email: "",
    phone: "",
    position: "",
    file: null,
  };

  const [formData, setFormData] = useState(initialFormData);
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      file: e.target.files[0],
    });
  };

  const handleJobSelect = (jobTitle) => {
    setFormData({
      ...formData,
      position: jobTitle,
    });
    setSelectedJob(jobTitle);
    // Scroll to form
    document.getElementById("application-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const API_URL = process.env.REACT_APP_API_URL || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData();
      form.append("name", formData.fullName);
      form.append("email", formData.email);
      form.append("phone", formData.phone);
      form.append("position", formData.position);
      form.append("resume", formData.file);

      const res = await fetch(`${API_URL}/career/createCareer`, {
        method: "POST",
        body: form,
      });

      if (!res.ok) {
        throw new Error("Failed to submit form");
      }

      const data = await res.json();
      console.log("Form Submitted:", data);
      setSubmitted(true);
      setFormData(initialFormData);
      fileInputRef.current.value = null;
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error("Error submitting form:", err);
      alert("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Award, title: "Competitive Salary", description: "Market-competitive compensation packages" },
    { icon: Heart, title: "Health Benefits", description: "Comprehensive health insurance coverage" },
    { icon: Zap, title: "Flexible Hours", description: "Work-life balance with flexible schedules" },
    { icon: GraduationCap, title: "Learning & Growth", description: "Continuous skill development opportunities" },
    { icon: Rocket, title: "Career Growth", description: "Clear paths for advancement" },
    { icon: Users, title: "Great Culture", description: "Collaborative and inclusive work environment" },
  ];

  const stats = [
    { value: "50+", label: "Team Members" },
    { value: "200+", label: "Projects Delivered" },
    { value: "98%", label: "Employee Satisfaction" },
    { value: "15+", label: "Countries Served" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" />

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <div className="inline-block" data-aos="fade-down" data-aos-delay="100">
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                Join Our Team
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Build Your Career{" "}
              <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                With Us
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              We're always looking for passionate individuals who want to make a difference in
              the IT industry. Explore our open positions and become part of our success story.
            </p>

            {/* Stats */}
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Work <span className="text-primary">With Us?</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground">
              We offer a comprehensive benefits package and a culture that values your growth
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group"
                >
                  <CardHeader>
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Current <span className="text-primary">Openings</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground">
              Explore our available positions and find the perfect role for you
            </p>
          </div>

          {/* Horizontal Scrollable Job Cards */}
          <div
            className="relative"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent hover:scrollbar-thumb-primary/40 transition-colors">
              <div className="flex gap-6 min-w-max px-4">
                {accordionData.map((job, index) => {
                  const jobDescription = job.sections.find((s) => s.title === "Job Description");
                  const requirements = job.sections.find((s) => s.title === "Requirements");
                  const perks = job.sections.find((s) => s.title === "Perks & Benefits");
                  const duration = job.sections.find((s) => s.title === "Duration");
                  const timings = job.sections.find((s) => s.title === "Timings");
                  const stipend = job.sections.find((s) => s.title === "Stipend");

                  return (
                    <Card
                      key={index}
                      className="w-[380px] min-w-[380px] border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group flex flex-col"
                    >
                      <CardHeader className="pb-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <Briefcase className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg font-bold mb-1 line-clamp-2">
                              {job.profile}
                            </CardTitle>
                            {duration && (
                              <CardDescription className="text-sm">
                                {duration.content}
                              </CardDescription>
                            )}
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="flex-1 flex flex-col space-y-4">
                        {/* Job Description Preview */}
                        {jobDescription && (
                          <div>
                            <h4 className="text-xs font-semibold text-primary mb-2 flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              Description
                            </h4>
                            <p className="text-xs text-muted-foreground line-clamp-3">
                              {jobDescription.type === "list"
                                ? jobDescription.content[0]
                                : jobDescription.content}
                            </p>
                          </div>
                        )}

                        {/* Key Requirements Preview */}
                        {requirements && (
                          <div>
                            <h4 className="text-xs font-semibold text-primary mb-2 flex items-center gap-1">
                              <Code className="h-3 w-3" />
                              Key Requirements
                            </h4>
                            <ul className="space-y-1">
                              {requirements.type === "list" &&
                                requirements.content.slice(0, 2).map((item, idx) => (
                                  <li key={idx} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                    <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                    <span className="line-clamp-1">{item}</span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}

                        {/* Quick Info */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                          {timings && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-primary flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium">Timings</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {timings.content}
                                </p>
                              </div>
                            </div>
                          )}
                          {stipend && (
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-primary flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium">Stipend</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {stipend.content}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Perks Preview */}
                        {perks && perks.type === "list" && (
                          <div className="flex flex-wrap gap-1.5">
                            {perks.content.slice(0, 3).map((perk, idx) => (
                              <span
                                key={idx}
                                className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                              >
                                {perk.split(" ")[0]}
                              </span>
                            ))}
                            {perks.content.length > 3 && (
                              <span className="text-xs text-muted-foreground px-2 py-1">
                                +{perks.content.length - 3} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Apply Button */}
                        <Button
                          onClick={() => handleJobSelect(job.profile)}
                          className="w-full mt-auto group/btn"
                          size="sm"
                        >
                          Apply Now
                          <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Scroll Indicator */}
            <div className="flex justify-center mt-6 gap-2">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>← Scroll to see more positions →</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recruitment Process Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-primary">Recruitment Process</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground">
              We follow a transparent and efficient recruitment process to ensure we find the best
              talent and provide a great candidate experience.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: "01",
                  title: "Submit Your Application",
                  description:
                    "Submit your resume and cover letter through our online application system.",
                  icon: FileText,
                  color: "text-blue-500",
                  bgGradient: "from-blue-500/10 to-cyan-500/10",
                },
                {
                  step: "02",
                  title: "Initial Screening",
                  description:
                    "Our HR team will review your application to assess your qualifications and experience. If your profile is relevant, our team will reach out to you for an initial screening call.",
                  icon: Users,
                  color: "text-purple-500",
                  bgGradient: "from-purple-500/10 to-pink-500/10",
                },
                {
                  step: "03",
                  title: "Video Interview",
                  description:
                    "If your application is selected, you will be invited for two to three rounds of video interviews. These interviews will help us learn more about you and your career goals.",
                  icon: Phone,
                  color: "text-green-500",
                  bgGradient: "from-green-500/10 to-emerald-500/10",
                },
                {
                  step: "04",
                  title: "Skills Assessment",
                  description:
                    "Finalists will complete a task or assessment to evaluate your technical skills and problem-solving abilities.",
                  icon: Code,
                  color: "text-orange-500",
                  bgGradient: "from-orange-500/10 to-amber-500/10",
                },
                {
                  step: "05",
                  title: "Cultural Fit Interview",
                  description:
                    "We'll assess your cultural fit and discuss salary details to determine if you're the right candidate for the role.",
                  icon: Heart,
                  color: "text-red-500",
                  bgGradient: "from-red-500/10 to-rose-500/10",
                },
                {
                  step: "06",
                  title: "Offer Letter",
                  description:
                    "If you're a good fit for the role, we'll extend a job offer.",
                  icon: Award,
                  color: "text-yellow-500",
                  bgGradient: "from-yellow-500/10 to-amber-500/10",
                },
                {
                  step: "07",
                  title: "Background Check",
                  description:
                    "We'll conduct a background check to verify your qualifications and employment history.",
                  icon: Shield,
                  color: "text-indigo-500",
                  bgGradient: "from-indigo-500/10 to-purple-500/10",
                },
                {
                  step: "08",
                  title: "Welcome to Oethical Technology!",
                  description:
                    "You've made it! We're excited to have you join our team.",
                  icon: Rocket,
                  color: "text-primary",
                  bgGradient: "from-primary/10 to-primary/5",
                  isFinal: true,
                },
              ].map((process, index) => {
                const Icon = process.icon;
                return (
                  <div
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={100 + index * 100}
                    className="relative"
                  >
                    {/* Connecting Line */}
                    {index < 7 && (
                      <div className="absolute left-8 top-20 w-0.5 h-full bg-border z-0" />
                    )}

                    <Card
                      className={cn(
                        "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl relative z-10",
                        process.isFinal && "border-primary border-2"
                      )}
                    >
                      <div className={cn("bg-gradient-to-br p-8", process.bgGradient)}>
                        <div className="flex items-start gap-6">
                          <div
                            className={cn(
                              "h-16 w-16 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center flex-shrink-0 shadow-lg",
                              process.isFinal && "ring-2 ring-primary"
                            )}
                          >
                            <Icon className={cn("h-8 w-8", process.color)} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                                Step {process.step}
                              </span>
                              {process.isFinal && (
                                <span className="text-xs font-medium text-primary bg-primary/20 px-3 py-1 rounded-full">
                                  Final Step
                                </span>
                              )}
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold mb-3">
                              {process.title}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                              {process.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Info */}
            <div className="space-y-8" data-aos="fade-right" data-aos-delay="100">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Apply <span className="text-primary">Now</span>
                </h2>
                <div className="w-16 h-1 bg-primary rounded-full mb-6" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Ready to take the next step in your career? Fill out the application form below
                  and our team will get back to you soon.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Quick Response</h3>
                    <p className="text-muted-foreground text-sm">
                      We typically respond within 24-48 hours during business days.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Secure Application</h3>
                    <p className="text-muted-foreground text-sm">
                      Your information is secure and will only be used for recruitment purposes.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Fair Process</h3>
                    <p className="text-muted-foreground text-sm">
                      We review all applications fairly and provide feedback to candidates.
                    </p>
                  </div>
                </div>
              </div>

              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg">Contact HR</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <a
                      href="mailto:hr@oethical.com"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      hr@oethical.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <a
                      href="tel:+919532912517"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      +91 9532912517
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">
                      Bhopal, Madhya Pradesh, India
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Form */}
            <Card
              className="border-border shadow-xl"
              data-aos="fade-left"
              data-aos-delay="200"
            >
              <CardHeader>
                <CardTitle className="text-2xl">Application Form</CardTitle>
                <CardDescription>
                  Please fill in all the required fields below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Application Submitted Successfully!
                    </h3>
                    <p className="text-muted-foreground">
                      Thank you for your interest. We'll review your application and get back to
                      you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <label htmlFor="fullName" className="text-sm font-medium">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        placeholder="John Doe"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-sm font-medium">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+91 1234567890"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="position" className="text-sm font-medium">
                        Position Applied <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        required
                        className={cn(
                          "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
                          "placeholder:text-muted-foreground",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          "disabled:cursor-not-allowed disabled:opacity-50"
                        )}
                      >
                        <option value="">Select a position</option>
                        {accordionData.map((item, index) => (
                          <option key={index} value={item.profile}>
                            {item.profile}
                          </option>
                        ))}
                      </select>
                      {selectedJob && (
                        <p className="text-xs text-primary mt-1">
                          Selected: {selectedJob}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="file" className="text-sm font-medium">
                        Resume Upload <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Input
                          id="file"
                          name="file"
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          required
                          accept=".pdf,.doc,.docx"
                          className="h-11 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                        />
                        <Upload className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Accepted formats: PDF, DOC, DOCX (Max 5MB)
                      </p>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full group"
                      disabled={loading}
                    >
                      {loading ? (
                        "Submitting..."
                      ) : (
                        <>
                          Submit Application
                          <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-t">
        <div className="container">
          <Card
            className="border-border shadow-2xl overflow-hidden"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-12 md:p-16">
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                  Don't See a Role That Fits?
                </h2>
                <p className="text-lg text-muted-foreground">
                  We're always looking for talented individuals. Send us your resume and we'll
                  keep you in mind for future opportunities.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Button size="lg" className="group" asChild>
                    <Link to="/contactUs">
                      Contact HR
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/team">
                      Meet Our Team
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Careers;
